import {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";

import {
  AlertTriangle,
  Wallet,
  Paperclip,
  Receipt,
  X,
  ExternalLink,
  Loader2,
  Eye,
} from "lucide-react";

import { toast } from "sonner";
import { Link } from "react-router-dom";

import ExportBar from "@/components/ExportBar";
import { formatCurrency } from "@/lib/formatters";
import { generateMonthlyPLPDF } from "@/lib/plStatementPDF";
import * as db from "@/lib/db";

/*
 * ============================================================
 * CONSTANTS
 * ============================================================
 */

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const monthBounds = (
  year,
  month
) => {
  const y =
    Number(year);

  const m =
    Number(month);

  const start =
    `${y}-${String(
      m
    ).padStart(
      2,
      "0"
    )}-01`;

  const lastDay =
    new Date(
      y,
      m,
      0
    ).getDate();

  const end =
    `${y}-${String(
      m
    ).padStart(
      2,
      "0"
    )}-${String(
      lastDay
    ).padStart(
      2,
      "0"
    )}`;

  return {
    start,
    end,
  };
};

/*
 * ============================================================
 * EVENT UNIT HELPERS
 * ============================================================
 */

const getRecordedUnits = (
  event
) =>
  Number(
    event?.totalProductQuantitySold ||
      0
  );

const getEstimatedUnits = (
  event
) =>
  Number(
    event?.estimatedUnrecordedWholeCups ||
      0
  );

const getTotalUnitsSold = (
  event
) =>
  getRecordedUnits(
    event
  ) +
  getEstimatedUnits(
    event
  );

/*
 * ============================================================
 * RECONCILIATION HELPERS
 * ============================================================
 */

const getReconciliationLabel = (
  status
) => {
  if (
    status ===
    "Fully Reconciled"
  ) {
    return "Fully reconciled";
  }

  if (
    status ===
    "Unrecorded Cup Sales Estimated"
  ) {
    return "Estimated from unrecorded sales";
  }

  if (
    status ===
    "Recorded Product Sales Exceed Total Sales Collected"
  ) {
    return "Recorded product sales exceed collected sales";
  }

  return "Manual review required";
};

const getReconciliationColor = (
  status
) => {
  if (
    status ===
    "Fully Reconciled"
  ) {
    return "text-emerald-700";
  }

  if (
    status ===
    "Unrecorded Cup Sales Estimated"
  ) {
    return "text-amber-700";
  }

  if (
    status ===
    "Recorded Product Sales Exceed Total Sales Collected"
  ) {
    return "text-red-700";
  }

  return "text-slate-500";
};

/*
 * ============================================================
 * MONTHLY P&L
 * ============================================================
 */

const MonthlyPL = () => {
  const today =
    new Date();

  const [
    year,
    setYear,
  ] = useState(
    String(
      today.getFullYear()
    )
  );

  const [
    month,
    setMonth,
  ] = useState(
    String(
      today.getMonth() +
        1
    )
  );

  const [
    availableYears,
    setAvailableYears,
  ] = useState([]);

  const [
    types,
    setTypes,
  ] = useState([]);

  const [
    categories,
    setCategories,
  ] = useState([]);

  const [
    taxTreatments,
    setTaxTreatments,
  ] = useState([]);

  const [
    activityTypes,
    setActivityTypes,
  ] = useState([]);

  const [
    monthEvents,
    setMonthEvents,
  ] = useState([]);

  const [
    monthTx,
    setMonthTx,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(
    true
  );

  const [
    openingCash,
    setOpeningCash,
  ] = useState(
    "0.00"
  );

  /*
   * ==========================================================
   * VIEW EVENT
   * ==========================================================
   */

  const [
    viewEvent,
    setViewEvent,
  ] = useState(
    null
  );

  /*
   * ==========================================================
   * VIEW TRANSACTION
   * ==========================================================
   */

  const [
    viewTransaction,
    setViewTransaction,
  ] = useState(
    null
  );

  const [
    viewAttachments,
    setViewAttachments,
  ] = useState([]);

  const [
    loadingViewAttachments,
    setLoadingViewAttachments,
  ] = useState(
    false
  );

  const [
    openingAttachmentId,
    setOpeningAttachmentId,
  ] = useState(
    null
  );

  /*
   * ==========================================================
   * MONTH BOUNDS
   * ==========================================================
   */

  const {
    start,
    end,
  } = useMemo(
    () =>
      monthBounds(
        year,
        month
      ),
    [
      year,
      month,
    ]
  );

  /*
   * ==========================================================
   * FETCH STATIC DATA
   * ==========================================================
   */

  const fetchStatic =
    useCallback(
      async () => {
        try {
          const [
            ttypes,
            cats,
            tax,
            ats,
            years,
          ] =
            await Promise.all([
              db.listTransactionTypes({
                includeArchived:
                  true,
              }),

              db.listTransactionCategories({
                includeArchived:
                  true,
              }),

              db.listTaxTreatmentStatuses({
                includeArchived:
                  true,
              }),

              db.listActivityTypes({
                includeArchived:
                  true,
              }),

              db.listEventYears(),
            ]);

          setTypes(
            ttypes
          );

          setCategories(
            cats
          );

          setTaxTreatments(
            tax
          );

          setActivityTypes(
            ats
          );

          const yset =
            new Set(
              years
            );

          yset.add(
            String(
              today.getFullYear()
            )
          );

          setAvailableYears(
            Array.from(
              yset
            ).sort(
              (
                a,
                b
              ) =>
                b.localeCompare(
                  a
                )
            )
          );
        } catch (
          error
        ) {
          console.error(
            error
          );

          toast.error(
            "Failed to load reference data"
          );
        }
      },
      []
    );

  /*
   * ==========================================================
   * FETCH MONTH
   * ==========================================================
   */

  const fetchMonth =
    useCallback(
      async () => {
        try {
          setLoading(
            true
          );

          const [
            txs,
            allEvents,
          ] =
            await Promise.all([
              db.listTransactions({
                from:
                  start,

                to:
                  end,

                sortBy:
                  "date_asc",
              }),

              db.listEventsSummary({
                month,

                year,

                sortBy:
                  "latest",

                eventStatus:
                  "completed",
              }),
            ]);

          setMonthTx(
            txs
          );

          setMonthEvents(
            allEvents
          );
        } catch (
          error
        ) {
          console.error(
            error
          );

          toast.error(
            "Failed to load month data"
          );
        } finally {
          setLoading(
            false
          );
        }
      },
      [
        start,
        end,
        month,
        year,
      ]
    );

  useEffect(
    () => {
      fetchStatic();
    },
    [
      fetchStatic,
    ]
  );

  useEffect(
    () => {
      fetchMonth();
    },
    [
      fetchMonth,
    ]
  );

  /*
   * ==========================================================
   * LOOKUPS
   * ==========================================================
   */

  const typeById = (
    id
  ) =>
    types.find(
      (
        item
      ) =>
        item.id ===
        id
    );

  const catById = (
    id
  ) =>
    categories.find(
      (
        item
      ) =>
        item.id ===
        id
    );

  const taxById = (
    id
  ) =>
    taxTreatments.find(
      (
        item
      ) =>
        item.id ===
        id
    );

  const activityTypeName = (
    id
  ) =>
    activityTypes.find(
      (
        item
      ) =>
        item.id ===
        id
    )?.name ||
    "—";

  /*
   * ==========================================================
   * TRANSACTION VIEW
   * ==========================================================
   */

  const openTransactionView =
    async (
      transaction
    ) => {
      setViewTransaction(
        transaction
      );

      setViewAttachments(
        []
      );

      setLoadingViewAttachments(
        true
      );

      try {
        const rows =
          await db.listAttachments(
            "transaction",
            transaction.id
          );

        setViewAttachments(
          rows ||
            []
        );
      } catch (
        error
      ) {
        console.error(
          error
        );

        toast.error(
          "Could not load receipt attachments"
        );
      } finally {
        setLoadingViewAttachments(
          false
        );
      }
    };

  const closeTransactionView =
    () => {
      setViewTransaction(
        null
      );

      setViewAttachments(
        []
      );

      setOpeningAttachmentId(
        null
      );
    };

  const openViewAttachment =
    async (
      attachment
    ) => {
      setOpeningAttachmentId(
        attachment.id
      );

      try {
        const url =
          await db.getAttachmentSignedUrl(
            attachment.filePath,
            3600
          );

        window.open(
          url,
          "_blank",
          "noopener,noreferrer"
        );
      } catch (
        error
      ) {
        console.error(
          error
        );

        toast.error(
          "Could not open receipt"
        );
      } finally {
        setOpeningAttachmentId(
          null
        );
      }
    };

  /*
   * ==========================================================
   * MONTHLY CALCULATIONS
   * ==========================================================
   */

  const computed =
    useMemo(
      () => {
        const byTreatment = {
          operating_expense:
            [],

          product_purchase_cash_only:
            [],

          asset_capitalized:
            [],

          owner_draw:
            [],

          prepaid_event_rental:
            [],

          pending:
            [],
        };

        monthTx.forEach(
          (
            transaction
          ) => {
            const transactionType =
              types.find(
                (
                  item
                ) =>
                  item.id ===
                  transaction.transactionTypeId
              );

            const category =
              categories.find(
                (
                  item
                ) =>
                  item.id ===
                  transaction.categoryId
              );

            const treatment =
              transactionType?.plTreatment ||
              "pending";

            const enriched = {
              ...transaction,

              _typeName:
                transactionType?.name ||
                "Uncategorised",

              _catName:
                category?.name ||
                "Uncategorised",

              _plTreatment:
                treatment,
            };

            if (
              byTreatment[
                treatment
              ]
            ) {
              byTreatment[
                treatment
              ].push(
                enriched
              );
            } else {
              byTreatment.pending.push(
                enriched
              );
            }
          }
        );

        /*
         * EVENTS
         */

        const evTotals =
          monthEvents.reduce(
            (
              acc,
              event
            ) => {
              acc.revenue +=
                Number(
                  event.totalSalesCollected ||
                    0
                );

              acc.productCost +=
                Number(
                  event.totalProductCost ||
                    0
                );

              acc.rental +=
                Number(
                  event.rentalCost ||
                    0
                );

              acc.transport +=
                Number(
                  event.transportCost ||
                    0
                );

              acc.other +=
                Number(
                  event.otherCost ||
                    0
                );

              acc.staff +=
                Number(
                  event.totalStaffWages ||
                    0
                );

              return acc;
            },
            {
              revenue:
                0,

              productCost:
                0,

              rental:
                0,

              transport:
                0,

              other:
                0,

              staff:
                0,
            }
          );

        const eventExpenses =
          evTotals.rental +
          evTotals.transport +
          evTotals.other +
          evTotals.staff;

        /*
         * OPERATING EXPENSES
         */

        const operatingExpensesTotal =
          byTreatment.operating_expense.reduce(
            (
              sum,
              transaction
            ) =>
              sum +
              Number(
                transaction.amount ||
                  0
              ),
            0
          );

        const isWoodRow = (
          row
        ) =>
          (
            row._typeName ||
            ""
          )
            .toLowerCase()
            .includes(
              "wood"
            ) ||
          (
            row._catName ||
            ""
          )
            .toLowerCase()
            .includes(
              "wood"
            );

        const woodIncentive =
          byTreatment.operating_expense
            .filter(
              isWoodRow
            )
            .reduce(
              (
                sum,
                transaction
              ) =>
                sum +
                Number(
                  transaction.amount ||
                    0
                ),
              0
            );

        /*
         * PRODUCT PURCHASES
         */

        const productPurchases =
          byTreatment.product_purchase_cash_only.reduce(
            (
              sum,
              transaction
            ) =>
              sum +
              Number(
                transaction.amount ||
                  0
              ),
            0
          );

        /*
         * P&L
         */

        const totalRevenue =
          evTotals.revenue;

        const grossProfit =
          totalRevenue -
          productPurchases;

        const grossMargin =
          totalRevenue >
          0
            ? (
                grossProfit /
                totalRevenue
              ) *
              100
            : 0;

        const netProfit =
          totalRevenue -
          productPurchases -
          eventExpenses -
          operatingExpensesTotal;

        const netMargin =
          totalRevenue >
          0
            ? (
                netProfit /
                totalRevenue
              ) *
              100
            : 0;

        /*
         * OWNER DRAW
         */

        const ownerDraws =
          byTreatment.owner_draw.reduce(
            (
              sum,
              transaction
            ) =>
              sum +
              Number(
                transaction.amount ||
                  0
              ),
            0
          );

        const profitRetained =
          netProfit -
          ownerDraws;

        /*
         * ASSETS
         */

        const assetPurchases =
          byTreatment.asset_capitalized.reduce(
            (
              sum,
              transaction
            ) =>
              sum +
              Number(
                transaction.amount ||
                  0
              ),
            0
          );

        /*
         * PREPAID EVENT RENTAL
         *
         * Paid now = cash outflow now.
         * Not deducted from payment month's P&L.
         *
         * Recognised later through the linked event.
         */

        const prepaidEventRentalPaid =
          byTreatment.prepaid_event_rental.reduce(
            (
              sum,
              transaction
            ) =>
              sum +
              Number(
                transaction.amount ||
                  0
              ),
            0
          );

        /*
         * PENDING CASH MOVEMENT
         */

        const pendingInflow =
          byTreatment.pending
            .filter(
              (
                transaction
              ) =>
                transaction.cashFlowDirection ===
                "inflow"
            )
            .reduce(
              (
                sum,
                transaction
              ) =>
                sum +
                Number(
                  transaction.amount ||
                    0
                ),
              0
            );

        const pendingOutflow =
          byTreatment.pending
            .filter(
              (
                transaction
              ) =>
                transaction.cashFlowDirection ===
                "outflow"
            )
            .reduce(
              (
                sum,
                transaction
              ) =>
                sum +
                Number(
                  transaction.amount ||
                    0
                ),
              0
            );

        /*
         * CASH SUMMARY
         */

        const cashReceived =
          totalRevenue +
          pendingInflow;

        const cashPayments =
          operatingExpensesTotal +
          productPurchases +
          assetPurchases +
          ownerDraws +
          prepaidEventRentalPaid +
          pendingOutflow;

        const netCashMovement =
          cashReceived -
          cashPayments;

        const opening =
          Number(
            openingCash
          ) ||
          0;

        const closingCash =
          opening +
          netCashMovement;

        /*
         * TRANSACTION LISTS
         */

        const deductibleTransactions =
          [
            ...byTreatment.product_purchase_cash_only,
            ...byTreatment.operating_expense,
          ].sort(
            (
              a,
              b
            ) =>
              (
                a.txDate ||
                ""
              ).localeCompare(
                b.txDate ||
                  ""
              )
          );

        const nonDeductibleTransactions =
          [
            ...byTreatment.asset_capitalized,
            ...byTreatment.owner_draw,
            ...byTreatment.prepaid_event_rental,
            ...byTreatment.pending,
          ].sort(
            (
              a,
              b
            ) =>
              (
                a.txDate ||
                ""
              ).localeCompare(
                b.txDate ||
                  ""
              )
          );

        const totalDeductible =
          productPurchases +
          operatingExpensesTotal;

        const totalNonDeductible =
          nonDeductibleTransactions.reduce(
            (
              sum,
              transaction
            ) =>
              sum +
              Number(
                transaction.amount ||
                  0
              ),
            0
          );

        return {
          byTreatment,

          evTotals,

          eventExpenses,

          operatingExpensesTotal,

          woodIncentive,

          totalRevenue,

          productPurchases,

          grossProfit,

          grossMargin,

          netProfit,

          netMargin,

          ownerDraws,

          profitRetained,

          assetPurchases,

          prepaidEventRentalPaid,

          pendingInflow,

          pendingOutflow,

          cashReceived,

          cashPayments,

          netCashMovement,

          closingCash,

          deductibleTransactions,

          nonDeductibleTransactions,

          totalDeductible,

          totalNonDeductible,
        };
      },
      [
        monthTx,
        monthEvents,
        types,
        categories,
        openingCash,
      ]
    );

  /*
   * ==========================================================
   * OPERATING EXPENSE BREAKDOWN
   * ==========================================================
   */

  const operatingByCategory =
    useMemo(
      () => {
        const map =
          {};

        computed.byTreatment.operating_expense.forEach(
          (
            transaction
          ) => {
            const label =
              transaction._catName ||
              "Uncategorised";

            map[
              label
            ] =
              (
                map[
                  label
                ] ||
                0
              ) +
              Number(
                transaction.amount ||
                  0
              );
          }
        );

        return Object.entries(
          map
        ).sort(
          (
            a,
            b
          ) =>
            b[
              1
            ] -
            a[
              1
            ]
        );
      },
      [
        computed
          .byTreatment
          .operating_expense,
      ]
    );

  const selectClass =
    "w-full bg-white border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-11 px-3 text-sm text-slate-800 appearance-none cursor-pointer transition-colors";

  const monthLabel =
    `${
      MONTH_NAMES[
        Number(
          month
        ) -
          1
      ]
    } ${year}`;

  /*
   * ==========================================================
   * VIEW EVENT CALCULATIONS
   * ==========================================================
   */

  const viewEventRevenue =
    Number(
      viewEvent?.totalSalesCollected ??
        viewEvent?.totalSales ??
        0
    );

  /*
   * COGS
   */

  const viewEventRecordedProductCost =
    Number(
      viewEvent?.totalProductCost ||
        0
    );

  const viewEventEstimatedUnrecordedCogs =
    Number(
      viewEvent?.estimatedUnrecordedCogs ||
        0
    );

  const viewEventTotalCogs =
    Number(
      viewEvent?.totalCostOfGoodsSold ??
        (
          viewEventRecordedProductCost +
          viewEventEstimatedUnrecordedCogs
        )
    );

  /*
   * EVENT EXPENSES
   */

  const viewEventRental =
    Number(
      viewEvent?.rentalCost ||
        0
    );

  const viewEventStaffWages =
    Number(
      viewEvent?.totalStaffWages ||
        0
    );

  const viewEventTransport =
    Number(
      viewEvent?.transportCost ||
        0
    );

  const viewEventOtherCost =
    Number(
      viewEvent?.otherCost ||
        0
    );

  const viewEventExpenses =
    viewEventRental +
    viewEventStaffWages +
    viewEventTransport +
    viewEventOtherCost;

  const viewEventProfit =
    Number(
      viewEvent?.netProfit ||
        0
    );

  /*
   * UNITS
   */

  const viewEventRecordedUnits =
    getRecordedUnits(
      viewEvent
    );

  const viewEventEstimatedUnits =
    getEstimatedUnits(
      viewEvent
    );

  const viewEventTotalUnits =
    getTotalUnitsSold(
      viewEvent
    );

  /*
   * ==========================================================
   * TRANSACTION LOOKUPS
   * ==========================================================
   */

  const viewTxType =
    viewTransaction
      ? typeById(
          viewTransaction.transactionTypeId
        )
      : null;

  const viewTxCategory =
    viewTransaction
      ? catById(
          viewTransaction.categoryId
        )
      : null;

  const viewTxTax =
    viewTransaction
      ? taxById(
          viewTransaction.taxTreatmentId
        )
      : null;

  /*
   * ==========================================================
   * RENDER
   * ==========================================================
   */

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24">

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 space-y-6">

        {/* =====================================================
            HEADER
        ====================================================== */}

        <div className="flex items-start justify-between gap-3 flex-wrap">

          <div>

            <h1
              className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#193f7d]"
              style={{
                fontFamily:
                  "Outfit, sans-serif",
              }}
            >
              Monthly P&amp;L
            </h1>

            <p className="text-sm text-slate-600 mt-1">
              Aggregated profit &amp; loss for{" "}
              {monthLabel}
            </p>

          </div>

          <ExportBar
            testIdPrefix="mpl-export"
            availableCSV={
              false
            }
            availableXLSX={
              false
            }
            pdfOverride={() => {
              const eventExpensesPayload = {
                rental:
                  computed.evTotals.rental,

                staff:
                  computed.evTotals.staff,

                commission:
                  0,

                transport:
                  computed.evTotals.transport,

                marketing:
                  0,

                other:
                  computed.evTotals.other,

                total:
                  computed.eventExpenses,
              };

              const companyOpexPayload = {
                items:
                  operatingByCategory.map(
                    ([
                      name,
                      amount,
                    ]) => ({
                      name,
                      amount,
                    })
                  ),

                total:
                  computed.operatingExpensesTotal,
              };

              const activitiesPayload =
                monthEvents.map(
                  (
                    event
                  ) => {
                    const eventExpenses =
                      Number(
                        event.rentalCost ||
                          0
                      ) +
                      Number(
                        event.transportCost ||
                          0
                      ) +
                      Number(
                        event.otherCost ||
                          0
                      ) +
                      Number(
                        event.totalStaffWages ||
                          0
                      );

                    return {
                      name:
                        event.eventName,

                      activityType:
                        activityTypeName(
                          event.activityTypeId
                        ),

                      revenue:
                        Number(
                          event.totalSalesCollected ||
                            0
                        ),

                      productCost:
                        Number(
                          event.totalProductCost ||
                            0
                        ),

                      eventExpenses,

                      netProfit:
                        Number(
                          event.netProfit ||
                            0
                        ),

                      netMargin:
                        Number(
                          event.profitMargin ||
                            0
                        ),
                    };
                  }
                );

              const eventExpenseBreakdown =
                [
                  {
                    category:
                      "Event Rental",

                    amount:
                      computed.evTotals.rental,
                  },

                  {
                    category:
                      "Staff Wages / Event Manpower",

                    amount:
                      computed.evTotals.staff,
                  },

                  {
                    category:
                      "Transport Expense",

                    amount:
                      computed.evTotals.transport,
                  },

                  {
                    category:
                      "Other Event Expenses",

                    amount:
                      computed.evTotals.other,
                  },
                ].filter(
                  (
                    row
                  ) =>
                    row.amount !==
                    0
                );

              const productPurchaseTransactions =
                computed.byTreatment.product_purchase_cash_only
                  .slice()
                  .sort(
                    (
                      a,
                      b
                    ) =>
                      (
                        a.txDate ||
                        ""
                      ).localeCompare(
                        b.txDate ||
                          ""
                      )
                  )
                  .map(
                    (
                      transaction
                    ) => ({
                      date:
                        transaction.txDate,

                      category:
                        transaction._catName ||
                        "Uncategorised",

                      description:
                        transaction.description ||
                        "",

                      supplier:
                        transaction.supplierCustomer ||
                        "",

                      amount:
                        Number(
                          transaction.amount ||
                            0
                        ),
                    })
                  );

              const companyOpexTransactions =
                computed.byTreatment.operating_expense
                  .slice()
                  .sort(
                    (
                      a,
                      b
                    ) =>
                      (
                        a.txDate ||
                        ""
                      ).localeCompare(
                        b.txDate ||
                          ""
                      )
                  )
                  .map(
                    (
                      transaction
                    ) => ({
                      date:
                        transaction.txDate,

                      category:
                        transaction._catName ||
                        "Uncategorised",

                      description:
                        transaction.description ||
                        "",

                      amount:
                        Number(
                          transaction.amount ||
                            0
                        ),
                    })
                  );

              generateMonthlyPLPDF({
                monthLabel,

                periodStart:
                  start,

                periodEnd:
                  end,

                totalRevenue:
                  computed.totalRevenue,

                totalProductCost:
                  computed.productPurchases,

                grossProfit:
                  computed.grossProfit,

                grossMargin:
                  computed.grossMargin,

                eventExpenses:
                  eventExpensesPayload,

                companyOpex:
                  companyOpexPayload,

                netProfit:
                  computed.netProfit,

                netMargin:
                  computed.netMargin,

                ownerDraws:
                  computed.ownerDraws,

                profitRetained:
                  computed.profitRetained,

                cashSummary: {
                  openingCash:
                    Number(
                      openingCash
                    ) ||
                    0,

                  cashReceived:
                    computed.cashReceived,

                  opexPaid:
                    computed.operatingExpensesTotal,

                  productPurchases:
                    computed.productPurchases,

                  assetPurchases:
                    computed.assetPurchases,

                  prepaidEventRentalPaid:
                    computed.prepaidEventRentalPaid,

                  ownerWithdrawals:
                    computed.ownerDraws,

                  pendingInflow:
                    computed.pendingInflow,

                  pendingOutflow:
                    computed.pendingOutflow,

                  netCashMovement:
                    computed.netCashMovement,

                  closingCash:
                    computed.closingCash,
                },

                activities:
                  activitiesPayload,

                eventExpenseBreakdown,

                productPurchaseTransactions,

                companyOpexTransactions,
              });
            }}
            buildReport={() =>
              null
            }
          />

        </div>

        {/* =====================================================
            MONTH / YEAR
        ====================================================== */}

        <section className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm">

          <div className="grid grid-cols-2 gap-3">

            <div>

              <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5 block">
                Month
              </label>

              <select
                value={
                  month
                }
                onChange={(
                  event
                ) =>
                  setMonth(
                    event.target.value
                  )
                }
                className={
                  selectClass
                }
              >

                {MONTH_NAMES.map(
                  (
                    monthName,
                    index
                  ) => (
                    <option
                      key={
                        index
                      }
                      value={String(
                        index +
                          1
                      )}
                    >
                      {
                        monthName
                      }
                    </option>
                  )
                )}

              </select>

            </div>

            <div>

              <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5 block">
                Year
              </label>

              <select
                value={
                  year
                }
                onChange={(
                  event
                ) =>
                  setYear(
                    event.target.value
                  )
                }
                className={
                  selectClass
                }
              >

                {availableYears.map(
                  (
                    yearOption
                  ) => (
                    <option
                      key={
                        yearOption
                      }
                      value={
                        yearOption
                      }
                    >
                      {
                        yearOption
                      }
                    </option>
                  )
                )}

              </select>

            </div>

          </div>

        </section>

        {loading ? (
          <div className="text-center py-12 text-slate-500">
            Calculating…
          </div>
        ) : (
          <>

            {/* =================================================
                1. BLUE P&L SUMMARY
            ================================================== */}

            <section className="bg-gradient-to-br from-[#193f7d] to-[#122f5e] text-white rounded-2xl p-5 sm:p-6 shadow-lg space-y-4">

              <PLRow
                label="Total Revenue"
                value={
                  computed.totalRevenue
                }
                big
              />

              <PLRow
                label="Less: Product / Stock Purchases"
                value={
                  -computed.productPurchases
                }
              />

              <PLRow
                label="Gross Profit"
                value={
                  computed.grossProfit
                }
                bold
              />

              <PLRow
                label="Gross Margin"
                value={`${computed.grossMargin.toFixed(
                  2
                )}%`}
                text
              />

              <div className="border-t border-white/20 pt-3 space-y-3">

                <PLRow
                  label="Less: Event / Activity Expenses"
                  subtitle="Rental · Manpower · Transport · Other Event Costs"
                  value={
                    -computed.eventExpenses
                  }
                />

                <PLRow
                  label="Less: Operating Expenses (incl. Wood Incentive)"
                  subtitle="Central Kitchen · Wood Incentive · Other Company Expenses"
                  value={
                    -computed.operatingExpensesTotal
                  }
                />

              </div>

              <div className="border-t border-white/30 pt-4 space-y-2">

                <PLRow
                  label="Monthly Net Profit / Loss"
                  value={
                    computed.netProfit
                  }
                  big
                  bold
                  color={
                    computed.netProfit >=
                    0
                      ? "text-emerald-300"
                      : "text-red-300"
                  }
                />

                <PLRow
                  label="Net Margin"
                  value={`${computed.netMargin.toFixed(
                    2
                  )}%`}
                  text
                />

              </div>

            </section>

            {/* =================================================
                2. ACTIVITIES
            ================================================== */}

            <section className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-sm space-y-3">

              <div className="flex items-center justify-between gap-3">

                <div>

                  <h2
                    className="text-base font-semibold text-slate-800"
                    style={{
                      fontFamily:
                        "Outfit, sans-serif",
                    }}
                  >
                    Activities in{" "}
                    {
                      monthLabel
                    }
                  </h2>

                  <p className="text-xs text-slate-500 mt-1">
                    Breakdown of monthly Revenue and Event Expenses
                  </p>

                </div>

                <span className="text-xs text-slate-500">
                  {
                    monthEvents.length
                  }{" "}
                  completed
                </span>

              </div>

              {monthEvents.length ===
              0 ? (
                <p className="text-sm text-slate-500 py-4">
                  No completed events in this month.
                </p>
              ) : (
                <>

                  <div className="divide-y divide-slate-100">

                    {monthEvents.map(
                      (
                        event
                      ) => {
                        const eventExpenses =
                          Number(
                            event.rentalCost ||
                              0
                          ) +
                          Number(
                            event.transportCost ||
                              0
                          ) +
                          Number(
                            event.otherCost ||
                              0
                          ) +
                          Number(
                            event.totalStaffWages ||
                              0
                          );

                        return (
                          <div
                            key={
                              event.id
                            }
                            className="py-3.5"
                          >

                            <div className="flex items-center justify-between gap-3">

                              <div className="min-w-0 flex-1">

                                <p className="text-sm font-medium text-slate-900 truncate">
                                  {
                                    event.eventName
                                  }
                                </p>

                                <p className="text-xs text-[#fa9d1c] font-semibold mt-1">
                                  {activityTypeName(
                                    event.activityTypeId
                                  )}
                                </p>

                              </div>

                              <div className="grid grid-cols-2 gap-6 text-right flex-shrink-0">

                                <div>

                                  <p className="text-sm font-mono font-bold text-slate-900">
                                    {formatCurrency(
                                      event.totalSalesCollected ||
                                        0
                                    )}
                                  </p>

                                  <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">
                                    Revenue
                                  </p>

                                </div>

                                <div>

                                  <p className="text-sm font-mono font-bold text-slate-900">
                                    {formatCurrency(
                                      eventExpenses
                                    )}
                                  </p>

                                  <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">
                                    Event Expenses
                                  </p>

                                </div>

                              </div>

                            </div>

                            <div className="flex justify-end mt-2">

                              <button
                                type="button"
                                onClick={() =>
                                  setViewEvent(
                                    event
                                  )
                                }
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-[#193f7d] hover:bg-blue-50 text-[11px] font-semibold text-[#193f7d] transition-colors"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                View Event P&amp;L
                              </button>

                            </div>

                          </div>
                        );
                      }
                    )}

                  </div>

                  <div className="border-t-2 border-slate-200 pt-4 mt-2">

                    <div className="grid grid-cols-2 gap-4">

                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">

                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                          Total Revenue
                        </p>

                        <p className="text-lg font-mono font-bold text-slate-900 mt-1">
                          {formatCurrency(
                            computed.totalRevenue
                          )}
                        </p>

                        <p className="text-[10px] text-slate-400 mt-1">
                          Matches Monthly P&amp;L Summary
                        </p>

                      </div>

                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">

                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                          Total Event Expenses
                        </p>

                        <p className="text-lg font-mono font-bold text-slate-900 mt-1">
                          {formatCurrency(
                            computed.eventExpenses
                          )}
                        </p>

                        <p className="text-[10px] text-slate-400 mt-1">
                          Matches Monthly P&amp;L Summary
                        </p>

                      </div>

                    </div>

                  </div>

                </>
              )}

            </section>

            {/* =================================================
                3. MONTHLY TRANSACTIONS
            ================================================== */}

            <section className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-sm space-y-5">

              <div className="flex items-start justify-between gap-3">

                <div>

                  <h2
                    className="text-base font-semibold text-slate-800"
                    style={{
                      fontFamily:
                        "Outfit, sans-serif",
                    }}
                  >
                    Monthly Transactions —{" "}
                    {
                      monthLabel
                    }
                  </h2>

                  <p className="text-xs text-slate-500 mt-1">
                    All transactions recorded in this month, separated by Monthly P&amp;L treatment
                  </p>

                </div>

                <Link
                  to="/transactions"
                  className="text-xs font-semibold text-[#193f7d] hover:text-[#fa9d1c]"
                >
                  View All →
                </Link>

              </div>

              {monthTx.length ===
              0 ? (
                <div className="text-center py-8 border border-dashed border-slate-200 rounded-xl">

                  <Receipt className="w-8 h-8 text-slate-300 mx-auto mb-2" />

                  <p className="text-sm text-slate-500">
                    No transactions recorded for{" "}
                    {
                      monthLabel
                    }.
                  </p>

                </div>
              ) : (
                <>

                  {/* DEDUCTIBLE */}

                  <div className="border border-emerald-200 rounded-xl overflow-hidden">

                    <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-200">

                      <div className="flex items-center justify-between gap-3">

                        <div>

                          <p className="text-xs font-bold uppercase tracking-wider text-emerald-800">
                            Deductible from Monthly P&amp;L
                          </p>

                          <p className="text-[11px] text-emerald-700 mt-1">
                            These transactions reduce Monthly Net Profit
                          </p>

                        </div>

                        <p className="text-lg font-mono font-bold text-emerald-800">
                          {formatCurrency(
                            computed.totalDeductible
                          )}
                        </p>

                      </div>

                    </div>

                    <div className="grid grid-cols-2 divide-x divide-slate-200 border-b border-slate-200">

                      <div className="p-3">

                        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                          Product / Stock Purchases
                        </p>

                        <p className="text-sm font-mono font-bold text-slate-900 mt-1">
                          {formatCurrency(
                            computed.productPurchases
                          )}
                        </p>

                      </div>

                      <div className="p-3">

                        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                          Operating Expenses
                        </p>

                        <p className="text-sm font-mono font-bold text-slate-900 mt-1">
                          {formatCurrency(
                            computed.operatingExpensesTotal
                          )}
                        </p>

                      </div>

                    </div>

                    <div className="divide-y divide-slate-100">

                      {computed.deductibleTransactions.map(
                        (
                          transaction
                        ) => (
                          <TransactionRow
                            key={
                              transaction.id
                            }
                            transaction={
                              transaction
                            }
                            onView={() =>
                              openTransactionView(
                                transaction
                              )
                            }
                          />
                        )
                      )}

                    </div>

                    <div className="flex items-center justify-between px-4 py-3 bg-emerald-50/60 border-t border-emerald-200">

                      <span className="text-xs font-bold uppercase tracking-wider text-emerald-800">
                        Total Deductible from Monthly P&amp;L
                      </span>

                      <span className="text-base font-mono font-bold text-emerald-800">
                        {formatCurrency(
                          computed.totalDeductible
                        )}
                      </span>

                    </div>

                  </div>

                  {/* NON-DEDUCTIBLE */}

                  <div className="border border-slate-200 rounded-xl overflow-hidden">

                    <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">

                      <div className="flex items-center justify-between gap-3">

                        <div>

                          <p className="text-xs font-bold uppercase tracking-wider text-slate-700">
                            Not Deducted from Monthly P&amp;L
                          </p>

                          <p className="text-[11px] text-slate-500 mt-1">
                            Recorded transactions that do not reduce Monthly Net Profit
                          </p>

                        </div>

                        <p className="text-lg font-mono font-bold text-slate-800">
                          {formatCurrency(
                            computed.totalNonDeductible
                          )}
                        </p>

                      </div>

                    </div>

                    <div className="divide-y divide-slate-100">

                      {computed.nonDeductibleTransactions.map(
                        (
                          transaction
                        ) => (
                          <TransactionRow
                            key={
                              transaction.id
                            }
                            transaction={
                              transaction
                            }
                            onView={() =>
                              openTransactionView(
                                transaction
                              )
                            }
                          />
                        )
                      )}

                    </div>

                    <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-200">

                      <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
                        Total Not Deducted from Monthly P&amp;L
                      </span>

                      <span className="text-base font-mono font-bold text-slate-900">
                        {formatCurrency(
                          computed.totalNonDeductible
                        )}
                      </span>

                    </div>

                  </div>

                </>
              )}

            </section>

            {/* =================================================
                4. PROFIT RETAINED
            ================================================== */}

            <section className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-sm space-y-3">

              <div className="flex items-center justify-between">

                <div>

                  <h2
                    className="text-base font-semibold text-slate-800"
                    style={{
                      fontFamily:
                        "Outfit, sans-serif",
                    }}
                  >
                    Profit Retained in Business
                  </h2>

                  <p className="text-xs text-slate-500 mt-1">
                    Monthly profit after owner withdrawals
                  </p>

                </div>

                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  P&amp;L View
                </span>

              </div>

              <RetRow
                label="Monthly Net Profit"
                value={
                  computed.netProfit
                }
              />

              <RetRow
                label="Less: Owner Withdrawals"
                value={
                  -computed.ownerDraws
                }
              />

              <div className="border-t border-slate-200 pt-3">

                <RetRow
                  label="Profit Retained in Business"
                  value={
                    computed.profitRetained
                  }
                  bold
                  big
                />

              </div>

              {computed.ownerDraws >
                computed.netProfit &&
                computed.netProfit >
                  0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 flex items-start gap-2">

                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />

                    Owner Withdrawals (
                    {formatCurrency(
                      computed.ownerDraws
                    )}
                    ) exceed Monthly Net Profit (
                    {formatCurrency(
                      computed.netProfit
                    )}
                    ). You are drawing from prior retained cash.

                  </div>
                )}

            </section>

            {/* =================================================
                5. OWNER WITHDRAWALS
            ================================================== */}

            <section className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-sm space-y-3">

              <div>

                <h2
                  className="text-base font-semibold text-slate-800"
                  style={{
                    fontFamily:
                      "Outfit, sans-serif",
                  }}
                >
                  Owner Withdrawals
                </h2>

                <p className="text-xs text-slate-500 mt-1">
                  Withdrawals recorded through Transactions
                </p>

              </div>

              {computed.byTreatment.owner_draw.length ===
              0 ? (
                <p className="text-sm text-slate-500 py-2">
                  No owner withdrawals recorded for{" "}
                  {
                    monthLabel
                  }.
                </p>
              ) : (
                <div className="divide-y divide-slate-100">

                  {computed.byTreatment.owner_draw.map(
                    (
                      transaction
                    ) => (
                      <TransactionRow
                        key={
                          transaction.id
                        }
                        transaction={
                          transaction
                        }
                        onView={() =>
                          openTransactionView(
                            transaction
                          )
                        }
                      />
                    )
                  )}

                  <div className="flex items-center justify-between py-3 border-t-2 border-slate-200">

                    <p className="text-sm font-bold text-slate-900">
                      Total Owner Withdrawals
                    </p>

                    <p className="text-sm font-mono font-bold text-slate-900">
                      {formatCurrency(
                        computed.ownerDraws
                      )}
                    </p>

                  </div>

                </div>
              )}

            </section>

            {/* =================================================
                6. CASH
            ================================================== */}

            <section className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-sm space-y-3">

              <div className="flex items-center justify-between">

                <div>

                  <h2
                    className="text-base font-semibold text-slate-800 flex items-center gap-2"
                    style={{
                      fontFamily:
                        "Outfit, sans-serif",
                    }}
                  >
                    <Wallet className="w-4 h-4" />
                    Cash Summary
                  </h2>

                  <p className="text-xs text-slate-500 mt-1">
                    Separate view of recorded cash movements
                  </p>

                </div>

                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Cash View
                </span>

              </div>

              <div className="grid grid-cols-2 gap-3">

                <div>

                  <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5 block">
                    Opening Cash Balance (RM)
                  </label>

                  <input
                    type="number"
                    step="0.01"
                    value={
                      openingCash
                    }
                    onChange={(
                      event
                    ) =>
                      setOpeningCash(
                        event.target.value
                      )
                    }
                    className={
                      selectClass
                    }
                  />

                </div>

              </div>

              <div className="space-y-1.5">

                <RetRow
                  label="Cash Received"
                  value={
                    computed.cashReceived
                  }
                />

                <RetRow
                  label="Operating Expenses Paid"
                  value={
                    -computed.operatingExpensesTotal
                  }
                />

                <RetRow
                  label="Product Purchases Paid"
                  value={
                    -computed.productPurchases
                  }
                />

                <RetRow
                  label="Asset Purchases Paid"
                  value={
                    -computed.assetPurchases
                  }
                />

                <RetRow
                  label="Prepaid Event Rental Paid"
                  value={
                    -computed.prepaidEventRentalPaid
                  }
                />

                <RetRow
                  label="Owner Withdrawals"
                  value={
                    -computed.ownerDraws
                  }
                />

              </div>

              <div className="border-t border-slate-200 pt-3 space-y-1.5">

                <RetRow
                  label="Net Cash Movement"
                  value={
                    computed.netCashMovement
                  }
                  bold
                />

                <RetRow
                  label="Closing Cash Balance"
                  value={
                    computed.closingCash
                  }
                  bold
                  big
                />

              </div>

            </section>

            {/* =================================================
                ABOUT
            ================================================== */}

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600 leading-relaxed space-y-2">

              <p>

                <strong>
                  About this Monthly P&amp;L:
                </strong>{" "}

                Revenue and Event Expenses are taken from completed events in{" "}
                {monthLabel}. Event Expenses include rental, manpower, transport and other event-specific expenses recorded in each Per Event P&amp;L.

              </p>

              <p>
                Product / Stock Purchases, including ingredients, cups and packaging, are taken from Transactions and reduce the Monthly P&amp;L. Monthly Operating Expenses, including Wood Profit-based Incentive and other company expenses, are also taken from Transactions.
              </p>

              <p>

                <strong>
                  Prepaid Event Rental does not reduce Net Profit in the month it is paid.
                </strong>{" "}

                It is shown as a cash outflow in the payment month and is recognised as Rental Expense through the exact linked Event P&amp;L when that event occurs.

              </p>

              <p>

                <strong>
                  Owner Withdrawals do not reduce Net Profit.
                </strong>{" "}

                They reduce Profit Retained in Business.{" "}

                <strong>
                  Equipment / Asset Purchases are capitalised
                </strong>{" "}

                and do not reduce Monthly Net Profit.

              </p>

              <p>

                <strong>
                  Cash Summary is separate from the P&amp;L.
                </strong>{" "}

                It shows recorded cash movements and does not change the Monthly Net Profit calculation.

              </p>

            </div>

          </>
        )}

      </div>

      {/* =======================================================
          EVENT P&L VIEW MODAL
      ======================================================== */}

      {viewEvent && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onMouseDown={() =>
            setViewEvent(
              null
            )
          }
        >

          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onMouseDown={(
              event
            ) =>
              event.stopPropagation()
            }
          >

            {/* HEADER */}

            <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-4 flex items-start justify-between rounded-t-2xl">

              <div className="min-w-0">

                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Event P&amp;L Summary
                </p>

                <h2
                  className="text-xl font-semibold text-[#193f7d] mt-1"
                  style={{
                    fontFamily:
                      "Outfit, sans-serif",
                  }}
                >
                  {
                    viewEvent.eventName
                  }
                </h2>

                <p className="text-xs text-slate-500 mt-1">

                  {activityTypeName(
                    viewEvent.activityTypeId
                  )}

                  {" · "}

                  {
                    viewEvent.startDate
                  }

                  {viewEvent.endDate &&
                  viewEvent.endDate !==
                    viewEvent.startDate
                    ? ` → ${viewEvent.endDate}`
                    : ""}

                </p>

              </div>

              <button
                type="button"
                onClick={() =>
                  setViewEvent(
                    null
                  )
                }
                className="p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-700"
              >
                <X className="w-5 h-5" />
              </button>

            </div>

            <div className="p-5 space-y-5">

              {/* =================================================
                  MAIN RESULT
              ================================================== */}

              <div
                className={`rounded-2xl border p-4 ${
                  viewEventProfit >=
                  0
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-red-50 border-red-200"
                }`}
              >

                <div className="grid grid-cols-2 gap-4">

                  <div>

                    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                      Net Profit / Loss
                    </p>

                    <p
                      className={`text-2xl font-mono font-bold mt-1 ${
                        viewEventProfit >=
                        0
                          ? "text-emerald-700"
                          : "text-red-600"
                      }`}
                    >
                      {formatCurrency(
                        viewEventProfit
                      )}
                    </p>

                  </div>

                  <div className="text-right">

                    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                      Net Margin
                    </p>

                    <p
                      className={`text-xl font-mono font-bold mt-1 ${
                        viewEventProfit >=
                        0
                          ? "text-emerald-700"
                          : "text-red-600"
                      }`}
                    >
                      {Number(
                        viewEvent.profitMargin ||
                          0
                      ).toFixed(
                        2
                      )}
                      %
                    </p>

                  </div>

                </div>

              </div>

              {/* =================================================
                  P&L SUMMARY
              ================================================== */}

              <div>

                <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-3">
                  P&amp;L Summary
                </h3>

                <div className="border border-slate-200 rounded-xl overflow-hidden">

                  {/* REVENUE */}

                  <SummaryRow
                    label="Revenue"
                    value={formatCurrency(
                      viewEventRevenue
                    )}
                  />

                  {/* COGS */}

                  <div className="border-t border-slate-100 bg-slate-50/60 px-3 py-3">

                    <div className="flex items-center justify-between gap-4">

                      <p className="text-sm font-bold text-slate-800">
                        Less: Cost of Goods Sold
                      </p>

                      <p className="font-mono text-sm font-bold text-slate-900">
                        {formatCurrency(
                          -viewEventTotalCogs
                        )}
                      </p>

                    </div>

                    <div className="mt-2 space-y-1.5 pl-2">

                      <BreakdownRow
                        label="Recorded Product Cost"
                        value={
                          viewEventRecordedProductCost
                        }
                      />

                      <BreakdownRow
                        label="Estimated Unrecorded COGS"
                        value={
                          viewEventEstimatedUnrecordedCogs
                        }
                      />

                      <div className="border-t border-slate-200 pt-1.5 mt-1.5">

                        <BreakdownRow
                          label="Total COGS"
                          value={
                            viewEventTotalCogs
                          }
                          strong
                        />

                      </div>

                    </div>

                  </div>

                  {/* EVENT EXPENSES */}

                  <div className="border-t border-slate-100 px-3 py-3">

                    <div className="flex items-center justify-between gap-4">

                      <p className="text-sm font-bold text-slate-800">
                        Less: Event Expenses
                      </p>

                      <p className="font-mono text-sm font-bold text-slate-900">
                        {formatCurrency(
                          -viewEventExpenses
                        )}
                      </p>

                    </div>

                    <div className="mt-2 space-y-1.5 pl-2">

                      <BreakdownRow
                        label="Rental"
                        value={
                          viewEventRental
                        }
                      />

                      <BreakdownRow
                        label="Staff Wages"
                        value={
                          viewEventStaffWages
                        }
                      />

                      <BreakdownRow
                        label="Transport"
                        value={
                          viewEventTransport
                        }
                      />

                      <BreakdownRow
                        label="Other Event Cost"
                        value={
                          viewEventOtherCost
                        }
                      />

                      <div className="border-t border-slate-200 pt-1.5 mt-1.5">

                        <BreakdownRow
                          label="Total Event Expenses"
                          value={
                            viewEventExpenses
                          }
                          strong
                        />

                      </div>

                    </div>

                  </div>

                  {/* FINAL RESULT */}

                  <div className="border-t border-slate-200 bg-slate-50">

                    <SummaryRow
                      label="Net Profit / Loss"
                      value={formatCurrency(
                        viewEventProfit
                      )}
                      strong
                    />

                    <div className="px-3 pb-3 flex items-center justify-between gap-4">

                      <p className="text-sm font-bold text-slate-900">
                        Net Margin
                      </p>

                      <p
                        className={`font-mono text-base font-bold ${
                          viewEventProfit >=
                          0
                            ? "text-emerald-700"
                            : "text-red-600"
                        }`}
                      >
                        {Number(
                          viewEvent.profitMargin ||
                            0
                        ).toFixed(
                          2
                        )}
                        %
                      </p>

                    </div>

                  </div>

                </div>

              </div>

              {/* =================================================
                  EVENT INFORMATION
              ================================================== */}

              <div>

                <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-3">
                  Event Information
                </h3>

                <div className="grid grid-cols-2 gap-4">

                  <QuickField
                    label="Activity Type"
                    value={activityTypeName(
                      viewEvent.activityTypeId
                    )}
                  />

                  <QuickField
                    label="Event Status"
                    value={
                      viewEvent.status ||
                      "—"
                    }
                  />

                  <QuickField
                    label="Event Days"
                    value={
                      viewEvent.totalEventDays ||
                      0
                    }
                  />

                  <div>

                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      Total Units Sold
                    </p>

                    <p className="text-lg font-mono font-bold text-slate-900 mt-1">
                      {
                        viewEventTotalUnits
                      }
                    </p>

                    <p className="text-[10px] text-slate-500 mt-1">

                      Recorded:{" "}

                      <span className="font-semibold">
                        {
                          viewEventRecordedUnits
                        }
                      </span>

                      {" · "}

                      Estimated Unrecorded:{" "}

                      <span className="font-semibold">
                        {
                          viewEventEstimatedUnits
                        }
                      </span>

                    </p>

                  </div>

                </div>

                {/* RECONCILIATION */}

                <div className="border-t border-slate-100 mt-4 pt-3">

                  <p
                    className={`text-[11px] ${getReconciliationColor(
                      viewEvent.reconciliationStatus
                    )}`}
                  >

                    <span className="font-semibold">
                      Reconciliation:
                    </span>{" "}

                    {getReconciliationLabel(
                      viewEvent.reconciliationStatus
                    )}

                  </p>

                </div>

              </div>

            </div>

          </div>

        </div>
      )}

      {/* =======================================================
          TRANSACTION VIEW MODAL
      ======================================================== */}

      {viewTransaction && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onMouseDown={
            closeTransactionView
          }
        >

          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onMouseDown={(
              event
            ) =>
              event.stopPropagation()
            }
          >

            {/* HEADER */}

            <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-4 flex items-start justify-between rounded-t-2xl">

              <div>

                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Transaction Details
                </p>

                <h2
                  className="text-xl font-semibold text-[#193f7d] mt-1"
                  style={{
                    fontFamily:
                      "Outfit, sans-serif",
                  }}
                >
                  {viewTransaction.description ||
                    "Transaction"}
                </h2>

              </div>

              <button
                type="button"
                onClick={
                  closeTransactionView
                }
                className="p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-700"
              >
                <X className="w-5 h-5" />
              </button>

            </div>

            <div className="p-5 sm:p-6 space-y-6">

              {/* AMOUNT */}

              <div className="bg-gradient-to-br from-[#193f7d] to-[#122f5e] rounded-2xl p-4 text-white">

                <div className="flex items-start justify-between">

                  <div>

                    <p className="text-[10px] uppercase tracking-wider font-bold text-white/60">
                      Amount
                    </p>

                    <p className="text-3xl font-mono font-bold mt-1">
                      {formatCurrency(
                        viewTransaction.amount
                      )}
                    </p>

                  </div>

                  <div className="text-right">

                    <p className="text-[10px] uppercase tracking-wider font-bold text-white/60">
                      Date
                    </p>

                    <p className="text-sm font-semibold mt-1">
                      {
                        viewTransaction.txDate
                      }
                    </p>

                  </div>

                </div>

              </div>

              {/* BASIC DETAILS */}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                <QuickField
                  label="Transaction Type"
                  value={
                    viewTxType?.name ||
                    "—"
                  }
                />

                <QuickField
                  label="Category"
                  value={
                    viewTxCategory?.name ||
                    "—"
                  }
                />

                <QuickField
                  label="Supplier / Customer"
                  value={
                    viewTransaction.supplierCustomer ||
                    "—"
                  }
                />

                <QuickField
                  label="Payment Method"
                  value={
                    viewTransaction.paymentMethod ||
                    "—"
                  }
                />

                <QuickField
                  label="Receipt Number"
                  value={
                    viewTransaction.receiptNumber ||
                    "—"
                  }
                />

                <QuickField
                  label="Tax Treatment"
                  value={
                    viewTxTax?.name ||
                    "—"
                  }
                />

                <QuickField
                  label="P&L Treatment"
                  value={
                    viewTxType?.plTreatment
                      ? viewTxType.plTreatment.replace(
                          /_/g,
                          " "
                        )
                      : "—"
                  }
                />

                {viewTransaction.businessUsePercentage !==
                  undefined &&
                  viewTransaction.businessUsePercentage !==
                    null &&
                  viewTransaction.businessUsePercentage !==
                    "" && (
                    <QuickField
                      label="Business Use"
                      value={`${viewTransaction.businessUsePercentage}%`}
                    />
                  )}

              </div>

              {/* NOTES */}

              {viewTransaction.notes && (
                <div>

                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                    Notes
                  </p>

                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm text-slate-700 whitespace-pre-wrap">
                    {
                      viewTransaction.notes
                    }
                  </div>

                </div>
              )}

              {/* RECEIPTS */}

              <div>

                <div className="flex items-center justify-between">

                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Receipt Attachments
                  </p>

                  {!loadingViewAttachments && (
                    <span className="text-xs text-slate-400">
                      {
                        viewAttachments.length
                      }{" "}
                      file
                      {viewAttachments.length ===
                      1
                        ? ""
                        : "s"}
                    </span>
                  )}

                </div>

                <div className="mt-3">

                  {loadingViewAttachments ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500 py-4">

                      <Loader2 className="w-4 h-4 animate-spin" />

                      Loading receipts…

                    </div>
                  ) : viewAttachments.length ===
                    0 ? (
                    <div className="border-2 border-dashed border-slate-200 rounded-xl p-5 text-center">

                      <Receipt className="w-8 h-8 text-slate-300 mx-auto mb-2" />

                      <p className="text-sm text-slate-500">
                        No receipt attachment found.
                      </p>

                    </div>
                  ) : (
                    <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden">

                      {viewAttachments.map(
                        (
                          attachment,
                          index
                        ) => (
                          <div
                            key={
                              attachment.id
                            }
                            className="flex items-center gap-3 p-3"
                          >

                            <Paperclip className="w-4 h-4 text-slate-500 flex-shrink-0" />

                            <div className="min-w-0 flex-1">

                              <p className="text-sm font-semibold text-slate-800 truncate">
                                {attachment.fileName ||
                                  `Receipt ${
                                    index +
                                    1
                                  }`}
                              </p>

                              <p className="text-[11px] text-slate-400 mt-0.5">

                                {attachment.fileType ||
                                  "Attachment"}

                                {attachment.fileSize
                                  ? ` · ${(
                                      attachment.fileSize /
                                      1024
                                    ).toFixed(
                                      0
                                    )} KB`
                                  : ""}

                              </p>

                            </div>

                            <button
                              type="button"
                              onClick={() =>
                                openViewAttachment(
                                  attachment
                                )
                              }
                              disabled={
                                openingAttachmentId ===
                                attachment.id
                              }
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-[#193f7d] hover:bg-blue-50 text-xs font-semibold text-[#193f7d] disabled:opacity-60"
                            >

                              {openingAttachmentId ===
                              attachment.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <ExternalLink className="w-3.5 h-3.5" />
                              )}

                              Open Receipt

                            </button>

                          </div>
                        )
                      )}

                    </div>
                  )}

                </div>

              </div>

            </div>

          </div>

        </div>
      )}

    </div>
  );
};

/*
 * ============================================================
 * P&L ROW
 * ============================================================
 */

const PLRow = ({
  label,
  subtitle = null,
  value,
  big = false,
  bold = false,
  text = false,
  color,
}) => {
  const display =
    text
      ? value
      : formatCurrency(
          value
        );

  const valueClass =
    color ||
    (
      typeof value ===
        "number" &&
      value <
        0
        ? "text-white/85"
        : "text-white"
    );

  return (
    <div className="flex items-center justify-between gap-4">

      <div className="min-w-0">

        <span
          className={`block text-white/80 ${
            big
              ? "text-sm"
              : "text-xs"
          } uppercase tracking-wider font-semibold`}
        >
          {
            label
          }
        </span>

        {subtitle && (
          <span className="block text-[10px] text-white/45 mt-1">
            {
              subtitle
            }
          </span>
        )}

      </div>

      <span
        className={`font-mono flex-shrink-0 ${
          bold
            ? "font-bold"
            : "font-semibold"
        } ${
          big
            ? "text-2xl"
            : "text-base"
        } ${valueClass}`}
      >
        {
          display
        }
      </span>

    </div>
  );
};

/*
 * ============================================================
 * RETAINED / CASH ROW
 * ============================================================
 */

const RetRow = ({
  label,
  value,
  big = false,
  bold = false,
}) => (
  <div className="flex items-center justify-between gap-4">

    <span
      className={`text-slate-600 ${
        big
          ? "text-sm"
          : "text-xs"
      } font-semibold uppercase tracking-wider`}
    >
      {
        label
      }
    </span>

    <span
      className={`font-mono ${
        bold
          ? "font-bold"
          : "font-semibold"
      } ${
        big
          ? "text-xl"
          : "text-sm"
      } ${
        value <
        0
          ? "text-red-600"
          : "text-slate-900"
      }`}
    >
      {formatCurrency(
        value
      )}
    </span>

  </div>
);

/*
 * ============================================================
 * TRANSACTION ROW
 * ============================================================
 */

const TransactionRow = ({
  transaction,
  onView,
}) => {
  const item =
    transaction;

  return (
    <div className="px-4 py-3">

      <div className="flex items-start justify-between gap-3">

        <div className="min-w-0 flex-1">

          <div className="flex items-center gap-2 flex-wrap">

            <span className="text-[11px] font-mono font-semibold text-slate-500">
              {
                item.txDate
              }
            </span>

            <TransactionBadge
              treatment={
                item._plTreatment
              }
              label={
                item._typeName
              }
            />

            {item.receiptStatus ===
              "Attached" && (
              <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">

                <Paperclip className="w-2.5 h-2.5" />

                Receipt

              </span>
            )}

            {item.receiptStatus ===
              "Missing" && (
              <span className="text-[9px] font-bold uppercase tracking-wider bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5">
                Receipt Missing
              </span>
            )}

          </div>

          <p className="text-sm font-semibold text-slate-900 mt-1.5">

            {item.description || (
              <span className="italic font-normal text-slate-400">
                No description
              </span>
            )}

          </p>

          <div className="flex items-center gap-1.5 flex-wrap text-[11px] text-slate-500 mt-1">

            <span>
              {
                item._catName
              }
            </span>

            {item.supplierCustomer && (
              <>

                <span className="text-slate-300">
                  ·
                </span>

                <span>
                  {
                    item.supplierCustomer
                  }
                </span>

              </>
            )}

            {item.paymentMethod && (
              <>

                <span className="text-slate-300">
                  ·
                </span>

                <span>
                  {
                    item.paymentMethod
                  }
                </span>

              </>
            )}

          </div>

        </div>

        <div className="text-right flex-shrink-0">

          <p className="text-sm sm:text-base font-mono font-bold text-slate-900">
            {formatCurrency(
              item.amount
            )}
          </p>

          <button
            type="button"
            onClick={
              onView
            }
            className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-[#193f7d] hover:bg-blue-50 text-[11px] font-semibold text-[#193f7d] transition-colors"
          >
            <Eye className="w-3.5 h-3.5" />
            View Transaction
          </button>

        </div>

      </div>

    </div>
  );
};

/*
 * ============================================================
 * TRANSACTION BADGE
 * ============================================================
 */

const TransactionBadge = ({
  treatment,
  label,
}) => {
  const styles = {
    operating_expense:
      "bg-red-50 text-red-700 border-red-200",

    product_purchase_cash_only:
      "bg-sky-50 text-sky-700 border-sky-200",

    asset_capitalized:
      "bg-violet-50 text-violet-700 border-violet-200",

    owner_draw:
      "bg-amber-50 text-amber-800 border-amber-200",

    prepaid_event_rental:
      "bg-blue-50 text-blue-700 border-blue-200",

    pending:
      "bg-slate-100 text-slate-600 border-slate-200",
  };

  return (
    <span
      className={`text-[9px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border ${
        styles[
          treatment
        ] ||
        styles.pending
      }`}
    >
      {
        label
      }
    </span>
  );
};

/*
 * ============================================================
 * SUMMARY ROW
 * ============================================================
 */

const SummaryRow = ({
  label,
  value,
  strong = false,
}) => (
  <div className="flex items-center justify-between gap-4 px-3 py-3">

    <p
      className={`text-sm ${
        strong
          ? "font-bold text-slate-900"
          : "font-medium text-slate-600"
      }`}
    >
      {
        label
      }
    </p>

    <p
      className={`font-mono ${
        strong
          ? "font-bold text-base"
          : "font-semibold text-sm"
      } text-slate-900`}
    >
      {
        value
      }
    </p>

  </div>
);

/*
 * ============================================================
 * P&L BREAKDOWN ROW
 * ============================================================
 */

const BreakdownRow = ({
  label,
  value,
  strong = false,
}) => (
  <div className="flex items-center justify-between gap-4">

    <p
      className={`text-xs ${
        strong
          ? "font-bold text-slate-700"
          : "font-medium text-slate-500"
      }`}
    >
      {
        label
      }
    </p>

    <p
      className={`font-mono text-xs ${
        strong
          ? "font-bold text-slate-900"
          : "font-semibold text-slate-700"
      }`}
    >
      {formatCurrency(
        value
      )}
    </p>

  </div>
);

/*
 * ============================================================
 * QUICK FIELD
 * ============================================================
 */

const QuickField = ({
  label,
  value,
}) => (
  <div>

    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
      {
        label
      }
    </p>

    <p className="text-sm font-semibold text-slate-800 mt-1 break-words">
      {
        value
      }
    </p>

  </div>
);

export default MonthlyPL;
